import { useCallback, useMemo } from 'react';
import log from 'loglevel';
import {
  pickBy,
  isNil,
  pick,
  complement,
  pipe,
  propEq,
  find,
  set,
  lensPath,
} from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';
import window from 'window';
import useReactRouter from 'use-react-router';
import qs from 'query-string';
import { CreditApplication, isOptInOrOptOut } from 'state/creditApplication';
import { useSelector } from 'react-redux';
import { selectTenant, Tenant } from 'state/tenant';
import useTranslate from './useTranslate';

export type DigitalData = {
  client: {
    clientName: string;
    pixelType: string;
  };
  page: {
    pageName: string;
  };
  application: {
    isSufficientPrefill: string;
    productChoiceSelection: string;
    status: string; //might make enum
    consumerSearchSuccessful: string;
    coBrandOptIn: string;
    coBrandOptOut: string;
    workflow: string;
    hasAccountAssure: string;
    hasAuthBuyer: string;
    prequalificationId: string;
    initialOffersShown: string;
    offersShown: string;
    initialOfferOnTop?: string;
    offerOnTop?: string;
    checkoutInSession?: string;
    initialIntendedPurchaseAmount?: string;
    intendedPurchaseAmount?: string;
    selectedCardName?: string;
    virtualCardIssuanceStatus?: string;
    virtualCardStatus?: string;
  };
  enhancedPresentment: {
    epId: string;
    callId: string;
    epPlacementId: string;
    epSessionId: string;
    epMessageId: string;
    location: string;
    urlPath: string;
    embedded: string;
    buyerId: string;
  };
};

export type OfferWidgetType =
  | 'installments'
  | 'splitpay'
  | 'prequalOffer'
  | 'notQualified'
  | 'prequalFallback'
  | 'prequalAccountFound';

export interface AnalyticsVariables {
  epId?: string;
  callId?: string;
  epPlacementId?: string;
  clientName?: string;
  pixelType?: string;
  urlPath?: string;
  epSessionId?: string;
  epMessageId?: string;
  location?: string;
  workflow?: string;
  embedded?: string;
  consumerSearchSuccessful?: string;
  isSufficientPrefill?: string;
  hasAccountAssure?: string;
  hasAuthBuyer?: string;
  productChoiceSelection?: string;
  coBrandOptIn?: string;
  coBrandOptOut?: string;
  pageName?: string;
  accountId?: string;
  applicationId?: string;
  divisionNumber?: string;
  prequalificationId?: string;
  breadBuyerId?: string;
  initialOffersShown?: string;
  offersShown?: string;
  initialOfferOnTop?: OfferWidgetType;
  offerOnTop?: OfferWidgetType;
  checkoutInSession?: string;
  initialIntendedPurchaseAmount?: string;
  intendedPurchaseAmount?: string;
  selectedCardName?: string;
  virtualCardIssuanceStatus?: string;
  virtualCardStatus?: string;
}

const mapAnalyticsVariables = {
  clientName: ['client', 'clientName'],
  pixelType: ['client', 'pixelType'],
  pageName: ['page', 'pageName'],
  isSufficientPrefill: ['application', 'isSufficientPrefill'],
  productChoiceSelection: ['application', 'productChoiceSelection'],
  consumerSearchSuccessful: ['application', 'consumerSearchSuccessful'],
  coBrandOptIn: ['application', 'coBrandOptIn'],
  coBrandOptOut: ['application', 'coBrandOptOut'],
  workflow: ['application', 'workflow'],
  hasAccountAssure: ['application', 'hasAccountAssure'],
  hasAuthBuyer: ['application', 'hasAuthBuyer'],
  epId: ['enhancedPresentment', 'epId'],
  callId: ['application', 'callId'],
  epPlacementId: ['enhancedPresentment', 'epPlacementId'],
  epSessionId: ['enhancedPresentment', 'epSessionId'],
  epMessageId: ['enhancedPresentment', 'epMessageId'],
  location: ['enhancedPresentment', 'location'],
  urlPath: ['enhancedPresentment', 'urlPath'],
  embedded: ['enhancedPresentment', 'embedded'],
  accountId: ['application', 'accountId'],
  applicationId: ['application', 'applicationId'],
  breadBuyerId: ['application', 'buyerId'],
  prequalificationId: ['application', 'prequalificationId'],
  divisionNumber: ['client', 'divisionNumber'],
  initialOffersShown: ['application', 'initialOffersShown'],
  offersShown: ['application', 'offersShown'],
  initialOfferOnTop: ['application', 'initialOfferOnTop'],
  offerOnTop: ['application', 'offerOnTop'],
  checkoutInSession: ['application', 'checkoutInSession'],
  initialIntendedPurchaseAmount: [
    'application',
    'initialIntendedPurchaseAmount',
  ],
  intendedPurchaseAmount: ['application', 'intendedPurchaseAmount'],
  selectedCardName: ['application', 'selectedCardName'],
  virtualCardIssuanceStatus: ['application', 'virtualCardIssuanceStatus'],
  virtualCardStatus: ['application', 'virtualCardStatus'],
} as const;

export type AnalyticsVariablesHook = () => {
  setAnalyticsVariablesOnLoad: () => void;
  setAnalyticsVariablesByApplication: (application: CreditApplication) => void;
  setAnalyticsVariables: (variables: AnalyticsVariables) => void;
};

const findProductChoice = (tenant: Tenant, productChoiceKey: string | null) => {
  if (!tenant.enabledFeatures.productChoices || !productChoiceKey) {
    return null;
  }
  return find(
    propEq('key', productChoiceKey),
    tenant.enabledFeatures.productChoices,
  );
};

export const useAnalyticsVariables: AnalyticsVariablesHook = () => {
  const tenant = useSelector(selectTenant);
  const { workflow, embedded } = useQueryParameters();
  const {
    location: { search },
  } = useReactRouter();
  const t = useTranslate();

  const query = useMemo(() => {
    const result = qs.parse(search, {
      parseBooleans: true,
    });
    result.embedded = embedded;
    result.workflow = workflow;

    return result;
  }, [search, embedded, workflow]);

  const setAnalyticsVariable = useCallback(
    (key: keyof AnalyticsVariables, value: string | undefined): void => {
      // Data layer
      window.digitalData = set(
        lensPath(mapAnalyticsVariables[key].flat()),
        value,
        window.digitalData,
      );
    },
    [],
  );

  const setAnalyticsVariables = useCallback(
    (variables: Partial<AnalyticsVariables>): void => {
      log.debug('Analytics', `setAnalyticsVariables`, variables);
      const existingKeys = Object.keys(variables);
      existingKeys.forEach((key, _) => {
        setAnalyticsVariable(
          key as keyof AnalyticsVariables,
          variables[key as keyof AnalyticsVariables],
        );
      });
    },
    [setAnalyticsVariable],
  );

  const setAnalyticsVariablesOnLoad = useCallback((): void => {
    const variables = pipe(
      pick([
        'epId',
        'epPlacementId',
        'clientName',
        'urlPath',
        'epSessionId',
        'epMessageId',
        'location',
        'workflow',
        'embedded',
        'prequalificationId',
      ]),
      pickBy(complement(isNil)),
    )(query) as Partial<AnalyticsVariables>;

    variables.consumerSearchSuccessful = 'false';
    variables.isSufficientPrefill = 'false';

    variables.divisionNumber = tenant.divisionNumber;
    if (tenant.enabledFeatures.pixelTracking) {
      variables.pixelType = tenant.enabledFeatures.pixelTracking.type;
    }

    if (tenant.divisionNumber) {
      setAnalyticsVariables({ divisionNumber: tenant.divisionNumber });
    }

    if (tenant.enabledFeatures.cardChoices && query['cardChoiceCode']) {
      const choices = tenant.enabledFeatures.cardChoices;
      const selectedCard = choices.find(
        choice => choice.code.toString() === query['cardChoiceCode'],
        query['cardChoiceCode'],
      );

      if (selectedCard) {
        setAnalyticsVariables({
          selectedCardName: t(`cardChoice.choices.${selectedCard.code}.label`),
        });
      }
    }

    if (tenant.enabledFeatures.cardSelector && query['selectedCardKey']) {
      const options = tenant.enabledFeatures.cardSelector.options;
      const selectedCard = options.find(
        option => option.key === query['selectedCardKey'],
        query['selectedCardKey'],
      );

      if (selectedCard) {
        setAnalyticsVariables({ selectedCardName: selectedCard.name });
      }
    }

    setAnalyticsVariables(variables);
  }, [
    query,
    tenant.divisionNumber,
    tenant.enabledFeatures.pixelTracking,
    tenant.enabledFeatures.cardSelector,
    tenant.enabledFeatures.cardChoices,
    setAnalyticsVariables,
    t,
  ]);

  const setAnalyticsVariablesByApplication = useCallback(
    (application: CreditApplication) => {
      if (tenant.enabledFeatures.accountAssure) {
        setAnalyticsVariables({
          hasAccountAssure: application.accountAssure ? 'true' : 'false',
        });
      }

      if (tenant.enabledFeatures.authBuyer) {
        setAnalyticsVariables({
          hasAuthBuyer: application.authBuyer ? 'true' : 'false',
        });
      }

      if (tenant.enabledFeatures.productChoices) {
        const choice = findProductChoice(tenant, application.productChoiceKey);
        if (choice)
          setAnalyticsVariables({ productChoiceSelection: choice.cardType });
      }

      if (tenant.enabledFeatures.optInOut) {
        const optInOrOut = isOptInOrOptOut(application, tenant);
        if (tenant.enabledFeatures.optInOut.type === 'optIn') {
          setAnalyticsVariables({
            coBrandOptIn: optInOrOut === 'optIn' ? 'true' : 'false',
          });
        } else {
          setAnalyticsVariables({
            coBrandOptOut: optInOrOut === 'optOut' ? 'true' : 'false',
          });
        }
      }
    },
    [setAnalyticsVariables, tenant],
  );

  return {
    setAnalyticsVariables,
    setAnalyticsVariablesOnLoad,
    setAnalyticsVariablesByApplication,
  };
};

export default useAnalyticsVariables;
